import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MapPin, Users, Globe, Navigation } from 'lucide-react';
import { Member } from '@/types/member';
import { geoNaturalEarth1, geoPath, geoGraticule } from 'd3-geo';
import * as topojson from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';

interface MemberMapViewProps {
  members: Member[];
  onMemberSelect: (member: Member) => void;
  onClusterSelect: (members: Member[]) => void;
}

interface MemberCluster {
  id: string;
  country: string;
  city: string;
  members: Member[];
  lat: number;
  lng: number;
}

// Comprehensive geocoding table — English names only
const COORDS: Record<string, [number, number]> = {
  // Countries [lat, lng]
  'Algeria': [28.03, 1.66],
  'France': [46.23, 2.21],
  'United Arab Emirates': [23.42, 53.85], 'UAE': [23.42, 53.85],
  'Qatar': [25.35, 51.18],
  'Saudi Arabia': [23.89, 45.08],
  'United Kingdom': [55.38, -3.44],
  'Ireland': [53.14, -7.69],
  'United States': [37.09, -95.71], 'USA': [37.09, -95.71],
  'Canada': [56.13, -106.35],
  'Morocco': [31.79, -7.09],
  'Tunisia': [33.89, 9.54],
  'Libya': [26.34, 17.23],
  'Egypt': [26.82, 30.80],
  'Turkey': [38.96, 35.24],
  'Germany': [51.17, 10.45],
  'Spain': [40.46, -3.75],
  'Italy': [41.87, 12.57],
  'Netherlands': [52.13, 5.29],
  'Belgium': [50.50, 4.47],
  'Switzerland': [46.82, 8.23],
  'Portugal': [39.40, -8.22],
  'Sweden': [60.13, 18.64],
  'Austria': [47.52, 14.55],
  'Poland': [51.92, 19.15],
  'Luxembourg': [49.82, 6.13],
  'Brazil': [-14.24, -51.93],
  'Senegal': [14.50, -14.45],
  "Côte d'Ivoire": [7.54, -5.55],
  'Cameroon': [7.37, 12.35],
  'Nigeria': [9.08, 8.68],
  'South Africa': [-30.56, 22.94],
  'Mali': [17.57, -4.00],
  'Niger': [17.61, 8.08],
  'China': [35.86, 104.20],
  'Japan': [36.20, 138.25],
  'India': [20.59, 78.96],
  'Australia': [-25.27, 133.78],
  'Russia': [61.52, 105.32],

  // Cities
  'Algiers': [36.75, 3.06], 'Oran': [35.70, -0.63], 'Constantine': [36.37, 6.61],
  'Annaba': [36.90, 7.77], 'Tizi Ouzou': [36.72, 4.05], 'Bejaia': [36.75, 5.08],
  'Blida': [36.47, 2.83], 'Tlemcen': [34.88, -1.32], 'Batna': [35.56, 6.17],
  'Nedroma': [35.01, -1.73], 'Rouiba': [36.73, 3.28], 'Mostaganem': [35.93, 0.09],
  'Paris': [48.86, 2.35], 'Lyon': [45.76, 4.84], 'Marseille': [43.30, 5.37],
  'Toulouse': [43.60, 1.44], 'Nice': [43.71, 7.26], 'Bordeaux': [44.84, -0.58],
  'Lille': [50.63, 3.06], 'Strasbourg': [48.57, 7.75], 'Nantes': [47.22, -1.55],
  'Montpellier': [43.61, 3.88], 'Houilles': [48.93, 2.19],
  'London': [51.51, -0.13], 'Dublin': [53.35, -6.26],
  'Dubai': [25.20, 55.27], 'Doha': [25.29, 51.53], 'Riyadh': [24.71, 46.67],
  'Casablanca': [33.57, -7.59], 'Rabat': [34.02, -6.83], 'Tunis': [36.81, 10.18],
  'Istanbul': [41.01, 28.98], 'Berlin': [52.52, 13.41], 'Munich': [48.14, 11.58],
  'Madrid': [40.42, -3.70], 'Barcelona': [41.39, 2.17],
  'Rome': [41.90, 12.50], 'Milan': [45.46, 9.19],
  'Amsterdam': [52.37, 4.90], 'Brussels': [50.85, 4.35],
  'Geneva': [46.20, 6.14], 'Zurich': [47.38, 8.54],
  'Montreal': [45.50, -73.57], 'Toronto': [43.65, -79.38],
  'New York': [40.71, -74.01], 'Boston': [42.36, -71.06],
  'San Francisco': [37.77, -122.42], 'Los Angeles': [34.05, -118.24],
  'Krakow': [50.06, 19.94],
  'Dakar': [14.72, -17.47], 'Lagos': [6.52, 3.38],
  'Tokyo': [35.68, 139.69],
};

const getCoords = (country: string, city?: string): [number, number] | null => {
  if (city && COORDS[city]) return COORDS[city];
  if (COORDS[country]) return COORDS[country];
  const key = Object.keys(COORDS).find(k =>
    country?.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(country?.toLowerCase() || '')
  );
  return key ? COORDS[key] : null;
};

const WIDTH = 960;
const HEIGHT = 500;

export const MemberMapView: React.FC<MemberMapViewProps> = ({
  members,
  onMemberSelect,
  onClusterSelect
}) => {
  const [selectedCluster, setSelectedCluster] = useState<MemberCluster | null>(null);
  const [hoveredCluster, setHoveredCluster] = useState<string | null>(null);
  const [worldData, setWorldData] = useState<any>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Load TopoJSON
  useEffect(() => {
    fetch('/data/countries-110m.json')
      .then(res => res.json())
      .then(data => setWorldData(data))
      .catch(err => console.error('Failed to load map data:', err));
  }, []);

  // D3 projection
  const projection = useMemo(() => 
    geoNaturalEarth1()
      .scale(160)
      .translate([WIDTH / 2, HEIGHT / 2])
  , []);

  const pathGenerator = useMemo(() => geoPath().projection(projection), [projection]);
  const graticule = useMemo(() => geoGraticule().step([20, 20])(), []);

  // Convert TopoJSON to a single merged landmass (no borders)
  const landPath = useMemo(() => {
    if (!worldData) return '';
    try {
      const land = topojson.merge(worldData as Topology, (worldData.objects.countries as any).geometries);
      return pathGenerator(land) || '';
    } catch {
      return '';
    }
  }, [worldData, pathGenerator]);

  // Cluster members by COUNTRY (not city) with normalization
  const memberClusters = useMemo(() => {
    const clusters: Record<string, MemberCluster> = {};
    members.forEach(member => {
      const country = (member.country || '').trim();
      if (!country) return;
      const coords = getCoords(country);
      if (!coords) {
        console.warn('⚠️ No coords for country:', country);
        return;
      }
      const key = country;
      if (!clusters[key]) {
        clusters[key] = {
          id: key,
          country,
          city: '',
          members: [],
          lat: coords[0],
          lng: coords[1],
        };
      }
      clusters[key].members.push(member);
    });
    return Object.values(clusters);
  }, [members]);

  const handleClusterClick = (cluster: MemberCluster) => {
    setSelectedCluster(cluster);
    onClusterSelect(cluster.members);
  };

  const getClusterRadius = (count: number) => {
    if (count >= 10) return 12;
    if (count >= 5) return 9;
    if (count >= 2) return 7;
    return 5;
  };

  const totalLocated = memberClusters.reduce((sum, c) => sum + c.members.length, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Carte Sans Frontières</h3>
                <p className="text-sm text-muted-foreground">
                  {totalLocated} membres • {memberClusters.length} localisations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { label: '1', color: 'bg-emerald-500' },
                { label: '2-4', color: 'bg-amber-500' },
                { label: '5-9', color: 'bg-primary' },
                { label: '10+', color: 'bg-destructive' },
              ].map(item => (
                <Badge key={item.label} variant="outline" className="text-xs gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  {item.label}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Real SVG Map */}
        <div className="lg:col-span-2">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <svg
                ref={svgRef}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                className="w-full h-auto"
                style={{ minHeight: 350, background: 'hsl(var(--muted))' }}
              >
                {/* Ocean */}
                <rect width={WIDTH} height={HEIGHT} fill="hsl(var(--muted))" />

                {/* Graticule (grid lines) */}
                <path
                  d={pathGenerator(graticule) || ''}
                  fill="none"
                  stroke="hsl(var(--border))"
                  strokeWidth="0.3"
                  opacity="0.4"
                />

                {/* Sphere outline */}
                <path
                  d={pathGenerator({ type: 'Sphere' } as any) || ''}
                  fill="none"
                  stroke="hsl(var(--border))"
                  strokeWidth="0.8"
                />

                {/* Landmass — no borders */}
                {landPath && (
                  <path
                    d={landPath}
                    fill="hsl(var(--card))"
                    stroke="hsl(var(--border))"
                    strokeWidth="0.3"
                  />
                )}

                {/* Connection lines to Algiers */}
                {memberClusters.length > 1 && (() => {
                  const algiersXY = projection([3.06, 36.75]);
                  if (!algiersXY) return null;
                  return memberClusters.map(cluster => {
                    const xy = projection([cluster.lng, cluster.lat]);
                    if (!xy) return null;
                    return (
                      <line
                        key={`line-${cluster.id}`}
                        x1={algiersXY[0]} y1={algiersXY[1]}
                        x2={xy[0]} y2={xy[1]}
                        stroke="hsl(var(--primary))"
                        strokeWidth="0.6"
                        strokeDasharray="4,3"
                        opacity="0.2"
                      />
                    );
                  });
                })()}

                {/* Member clusters */}
                {memberClusters.map(cluster => {
                  const xy = projection([cluster.lng, cluster.lat]);
                  if (!xy) return null;
                  const [cx, cy] = xy;
                  const r = getClusterRadius(cluster.members.length);
                  const isHovered = hoveredCluster === cluster.id;
                  const isSelected = selectedCluster?.id === cluster.id;
                  const count = cluster.members.length;

                  const fillColor = count >= 10
                    ? 'hsl(var(--destructive))'
                    : count >= 5
                    ? 'hsl(var(--primary))'
                    : count >= 2
                    ? 'hsl(38 92% 50%)'  // amber
                    : 'hsl(160 84% 39%)'; // emerald

                  return (
                    <g
                      key={cluster.id}
                      onClick={() => handleClusterClick(cluster)}
                      onMouseEnter={() => setHoveredCluster(cluster.id)}
                      onMouseLeave={() => setHoveredCluster(null)}
                      className="cursor-pointer"
                    >
                      {/* Pulse for selected */}
                      {isSelected && (
                        <circle cx={cx} cy={cy} r={r + 4} fill="none"
                          stroke={fillColor} strokeWidth="1.5" opacity="0.5">
                          <animate attributeName="r" from={r + 3} to={r + 14} dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                      )}

                      {/* Shadow */}
                      <circle cx={cx} cy={cy + 1} r={isHovered ? r + 2 : r} fill="black" opacity="0.15" />

                      {/* Dot */}
                      <circle
                        cx={cx} cy={cy}
                        r={isHovered ? r + 2 : r}
                        fill={fillColor}
                        stroke="hsl(var(--card))"
                        strokeWidth="1.5"
                        style={{ transition: 'r 0.15s ease' }}
                      />

                      {/* Count */}
                      <text
                        x={cx} y={cy + 1}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={r >= 9 ? '9' : '7'}
                        fontWeight="700"
                        style={{ pointerEvents: 'none' }}
                      >
                        {count}
                      </text>

                      {/* Tooltip */}
                      {isHovered && (
                        <g style={{ pointerEvents: 'none' }}>
                          <rect
                            x={cx - 55} y={cy - r - 24}
                            width="110" height="18"
                            rx="3"
                            fill="hsl(var(--foreground))"
                            opacity="0.92"
                          />
                          <text
                            x={cx} y={cy - r - 12.5}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="hsl(var(--background))"
                            fontSize="8"
                            fontWeight="500"
                          >
                            {cluster.country} ({cluster.members.length})
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-primary" />
                {selectedCluster
                  ? (selectedCluster.city ? `${selectedCluster.city}, ${selectedCluster.country}` : selectedCluster.country)
                  : 'Sélectionnez une région'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {selectedCluster ? (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Membres</span>
                    <Badge>{selectedCluster.members.length}</Badge>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedCluster.members.map(member => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-2.5 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => onMemberSelect(member)}
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={member.image} alt={member.name} />
                          <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {member.name?.charAt(0) || 'M'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{member.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.role}{member.company ? ` — ${member.company}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 space-y-3">
                  <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto">
                    <Navigation className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Explorez le réseau</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cliquez sur un point pour voir les membres
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top locations */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Top Localisations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {memberClusters
                .sort((a, b) => b.members.length - a.members.length)
                .slice(0, 6)
                .map(cluster => (
                  <button
                    key={cluster.id}
                    className="flex items-center justify-between w-full p-2 rounded-md hover:bg-muted/50 transition-colors text-sm"
                    onClick={() => handleClusterClick(cluster)}
                  >
                    <span className="text-foreground truncate">
                      {cluster.city ? `${cluster.city}, ${cluster.country}` : cluster.country}
                    </span>
                    <Badge variant="secondary" className="text-xs ml-2 flex-shrink-0">
                      {cluster.members.length}
                    </Badge>
                  </button>
                ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
